/**
 * ProspectStageControl — the ONE way to change a lead's stage (list row + lead
 * page). Design rule 2026 §9.1: consequential action = labeled dropdown, not
 * one-tap chips.
 *
 *  - choosing "lost" opens an inline reason panel (optional reason)
 *  - choosing "registered" on an unlinked lead opens LinkClinicPicker
 *  - everything else saves immediately
 *
 * Props: prospect, author, compact (row variant: no label, smaller trigger)
 */

import { useEffect, useRef, useState } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";
import { useToast } from "../../../../contexts/ToastContext";
import { useUpdateProspectStageMutation } from "../../../../store/services/nivApi";
import {
  PROSPECT_STAGES,
  PROSPECT_STAGE_ORDER,
  PROSPECT_STAGE_LABELS,
  PROSPECT_STAGE_COLORS,
} from "../../../../services/nivFirebase";
import LinkClinicPicker from "../lead/LinkClinicPicker";

const rtkErrorMessage = (error) =>
  error?.message || error?.data?.message || "אירעה שגיאה, נסה שוב";

function ProspectStageControl({ prospect, author, compact = false }) {
  const { showSuccess, showError } = useToast();
  const [open, setOpen] = useState(false);
  const [lostPanel, setLostPanel] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const wrapRef = useRef(null);
  const [updateStage, { isLoading }] = useUpdateProspectStageMutation();

  const stage = prospect.stage || PROSPECT_STAGES.LEAD;
  const color = PROSPECT_STAGE_COLORS[stage] || "#9ca3af";

  // Outside click / touch closes the menu
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const save = async (nextStage, reason) => {
    try {
      await updateStage({
        phone: prospect.id,
        stage: nextStage,
        author,
        ...(nextStage === PROSPECT_STAGES.LOST ? { lostReason: reason } : {}),
      }).unwrap();
      setLostPanel(false);
      setLostReason("");
      showSuccess("הסטטוס עודכן");
    } catch (error) {
      console.error("[CK-9968] Error updating prospect stage:", {
        errorCode: "CK-9968",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(rtkErrorMessage(error));
    }
  };

  const handlePick = (nextStage) => {
    setOpen(false);
    if (nextStage === stage) return;
    if (nextStage === PROSPECT_STAGES.LOST) {
      setLostPanel(true);
      return;
    }
    if (nextStage === PROSPECT_STAGES.REGISTERED && !prospect.clinicId) {
      setShowPicker(true);
      return;
    }
    save(nextStage);
  };

  return (
    <div
      className={`nivcl-stage-select${compact ? " is-compact" : ""}`}
      ref={wrapRef}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {!compact && <span className="nivcl-stage-select-label">שינוי סטטוס</span>}
      <button
        type="button"
        className={`nivcl-stage-trigger${open ? " open" : ""}`}
        style={{ backgroundColor: `${color}1f`, color, borderColor: `${color}55` }}
        onClick={() => setOpen((v) => !v)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="nivcl-stage-trigger-value">
          <span className="nivcl-stage-dot" style={{ backgroundColor: color }} />
          {PROSPECT_STAGE_LABELS[stage] || stage}
        </span>
        <CaretDown size={13} weight="bold" className={`nivcl-stage-caret${open ? " open" : ""}`} />
      </button>

      {open && (
        <div className="nivcl-stage-menu" role="listbox">
          {PROSPECT_STAGE_ORDER.map((s) => {
            const c = PROSPECT_STAGE_COLORS[s];
            const active = s === stage;
            return (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={active}
                className={`nivcl-stage-option${active ? " active" : ""}`}
                onClick={() => handlePick(s)}
                disabled={active || isLoading}
              >
                <span className="nivcl-stage-option-dot" style={{ backgroundColor: c }} />
                <span className="nivcl-stage-option-label">{PROSPECT_STAGE_LABELS[s]}</span>
                {active && <Check size={13} weight="bold" />}
              </button>
            );
          })}
        </div>
      )}

      {lostPanel && (
        <div className="nivcl-inline-panel nivcl-stage-lost-panel">
          <input
            type="text"
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            placeholder="סיבה (לא חובה)"
            autoFocus
          />
          <div className="nivcl-inline-panel-actions">
            <button
              type="button"
              className="nivcl-secondary-btn"
              disabled={isLoading}
              onClick={() => save(PROSPECT_STAGES.LOST, lostReason)}
            >
              סמן כלא רלוונטי
            </button>
            <button type="button" className="nivcl-ghost-btn" onClick={() => setLostPanel(false)}>
              ביטול
            </button>
          </div>
        </div>
      )}

      {showPicker && (
        <LinkClinicPicker prospect={prospect} author={author} onClose={() => setShowPicker(false)} />
      )}
    </div>
  );
}

export default ProspectStageControl;
